import React, { useEffect, useRef, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useParams, useRouteLoaderData } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { GameFeaturingLaunchManager, GameFeaturingLaunchState, useChoiceLabel } from "@/const";
import { useRequest } from "@components/AppHook";
import GameFeaturingLaunchPromoStateDisplay, {
    getColorByState,
} from "@components/Displays/GameFeaturingLaunchPromoStateDisplay";
import { DevForm } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { Awesome, DevModal } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { CommonCard, ListingCard } from "@components/Pages";
import { buildQS, humanTimer, parseAndLinkify, url_reverse } from "@components/Utils";

const ConfirmModal = ({ show, setShow, handle, setBtnRef, buttonRef }) => {
    const { t } = useTranslation();
    const handleClose = () => {
        setShow(false);
        setBtnRef(null);
    };

    return (
        <DevModal
            show={show}
            onHide={handleClose}
            header={t("Модерация")}
            body={
                buttonRef.current.value === "approve"
                    ? t("Подтвердите одобрение заявки")
                    : t("Подтвердите отклонение заявки")
            }
            footer={
                <>
                    <DevForm.Button
                        variant="primary"
                        name={buttonRef.current.name}
                        value={buttonRef.current.value}
                        onClick={handle}
                    >
                        {t("Отправить")}
                    </DevForm.Button>
                    <Button variant="secondary" onClick={handleClose}>
                        {t("Отмена")}
                    </Button>
                </>
            }
        />
    );
};

const StateChangeConfirmModal = ({ show, setShow, state, promoId, prevState, setCurrentState }) => {
    const { t } = useTranslation();
    const { api_post } = useRequest();

    const handleClose = () => {
        setShow(false);
        setCurrentState(prevState);
    };

    const submitHandle = (ev) => {
        api_post(ev, url_reverse("api:moderation:featuring:list", { kind: "launch" }), {
            params: { id: promoId, state },
            then: () => setShow(false),
        });
    };

    return (
        <DevModal
            show={show}
            onHide={handleClose}
            header={t("Смена статуса")}
            body={t("Подтвердите смену статуса")}
            footer={
                <>
                    <DevForm.Button variant="primary" name="submit" onClick={submitHandle}>
                        {t("Отправить")}
                    </DevForm.Button>
                    <Button variant="secondary" onClick={handleClose}>
                        {t("Отмена")}
                    </Button>
                </>
            }
        />
    );
};

const StateSelect = ({ state, promoId }) => {
    const [currentState, setCurrentState] = useState(state);
    const [show, setShow] = useState(false);
    const GameFeaturingLaunchStateTrans = useChoiceLabel("GameFeaturingLaunchState");
    const choices = Object.keys(GameFeaturingLaunchState).reduce((acc, key) => {
        if (GameFeaturingLaunchStateTrans[GameFeaturingLaunchState[key]]) {
            acc[GameFeaturingLaunchState[key]] = GameFeaturingLaunchStateTrans[GameFeaturingLaunchState[key]];
        }
        return acc;
    }, {});

    const dot = (color = "transparent") => ({
        alignItems: "center",
        display: "flex",

        ":before": {
            backgroundColor: color,
            borderRadius: 10,
            content: '" "',
            display: "block",
            marginRight: 8,
            height: 10,
            width: 10,
        },
    });

    const colourStyles = {
        input: (styles) => ({ ...styles, ...dot() }),
        singleValue: (styles, { data }) => ({ ...styles, ...dot(getColorByState(data.value)) }),
    };

    return (
        <>
            <DevForm.Select
                styles={colourStyles}
                clearable={false}
                choices={choices}
                onChange={(ev) => {
                    setShow(true);
                    setCurrentState(ev ? ev.value : "");
                }}
                value={currentState}
                menuPortalTarget={document.body} // решает проблему z-index с чойсами
            />
            <StateChangeConfirmModal
                show={show}
                setShow={setShow}
                state={currentState}
                prevState={state}
                setCurrentState={setCurrentState}
                promoId={promoId}
            />
        </>
    );
};

const ModeratorChangeConfirmModal = ({ show, setShow, manager, promoId, prevManager, setCurrentManager }) => {
    const { t } = useTranslation();
    const { api_post } = useRequest();

    const handleClose = () => {
        setShow(false);
        setCurrentManager(prevManager);
    };

    const submitHandle = (ev) => {
        api_post(ev, url_reverse("api:moderation:featuring:list", { kind: "launch" }), {
            params: { id: promoId, manager },
            then: () => setShow(false),
        });
    };

    return (
        <DevModal
            show={show}
            onHide={handleClose}
            header={t("Смена менеджера")}
            body={t("Подтвердите смену менеджера")}
            footer={
                <>
                    <DevForm.Button variant="primary" name="submit" onClick={submitHandle}>
                        {t("Отправить")}
                    </DevForm.Button>
                    <Button variant="secondary" onClick={handleClose}>
                        {t("Отмена")}
                    </Button>
                </>
            }
        />
    );
};

const ModeratorSelect = ({ promoId, manager }) => {
    const { t } = useTranslation();
    const [show, setShow] = useState(false);
    const [currentManager, setCurrentManager] = useState(manager);

    const choices = { "": t("Не выбрано") };
    GameFeaturingLaunchManager.forEach((o) => {
        choices[o] = o;
    });

    return (
        <>
            <DevForm.Select
                choices={choices}
                value={currentManager}
                onChange={(ev) => {
                    setShow(true);
                    setCurrentManager(ev ? ev.value : "");
                }}
                menuPortalTarget={document.body} // решает проблему z-index с чойсами
            />
            <ModeratorChangeConfirmModal
                show={show}
                setShow={setShow}
                manager={currentManager}
                prevManager={manager}
                setCurrentManager={setCurrentManager}
                promoId={promoId}
            />
        </>
    );
};

const DownloadPromoFilesButton = ({ data }) => {
    const { t } = useTranslation();
    const downloadPromoHandle = () => {
        const qs = "?" + buildQS({ get_files: 1 });
        const url = url_reverse("api:moderation:featuring:edit", {
            kind: "launch",
            featuring_id: data.id,
        });
        window.open(url + qs);
    };

    const flag = ["popup_picture", "banner1_picture", "banner2_picture", "banner2_picture_2"].some(
        (item) => data[item] && data[item] !== ""
    );

    if (flag) {
        return (
            <DevForm.Button
                name="download_promo"
                size="sm"
                onClick={downloadPromoHandle}
                tooltip={t("Скачать все промоматериалы")}
            >
                <Awesome type={"save"} />
            </DevForm.Button>
        );
    }

    return <></>;
};

const TableActionButtons = ({ item }) => {
    const { setDelData, apiData } = useAppContext();
    const { t } = useTranslation();

    return (
        <div className="d-flex justify-content-end">
            <DownloadPromoFilesButton data={item} />
            {apiData?.allow_delete && (
                <DevForm.Button
                    variant="outline-danger"
                    className="ml-1"
                    name="del_featuring"
                    size="sm"
                    tooltip={t("Удалить")}
                    onClick={() =>
                        setDelData({
                            url: url_reverse("api:moderation:featuring:delete", {
                                kind: "launch",
                                featuring_id: item.id,
                            }),
                        })
                    }
                >
                    <Awesome type="trash-o" />
                </DevForm.Button>
            )}
        </div>
    );
};

function List({ header }) {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:moderation:featuring:list", { kind: "launch" }));
    const commonInfo = useRouteLoaderData("commonInfo");

    if (!apiData) return;

    return (
        <ListingCard header={header}>
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Игра")}</th>
                        <th>{t("Менеджер")}</th>
                        <th>{t("Статус")}</th>
                        <th>{t("Время ожидания")}</th>
                        <th>{t("Карманы")}</th>
                        <th>{t("Дата начала")}</th>
                        <th width="120px" />
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((o) => (
                        <tr key={`object_list${o.id}`}>
                            <td>
                                <Link
                                    to={url_reverse("admin:moderation:featuring:edit", {
                                        kind: "launch",
                                        featuring_id: o.id,
                                    })}
                                >
                                    {o.game.base_locale_object.name}
                                </Link>
                            </td>
                            <td>
                                <ModeratorSelect promoId={o.id} manager={o.manager} />
                            </td>
                            <td>
                                <StateSelect promoId={o.id} state={o.state} />
                            </td>
                            <td>
                                {o.state_modified && !o.is_launched && humanTimer(o.state_modified, commonInfo.now)}
                            </td>
                            <td>
                                {o.pocket_data &&
                                    Object.keys(o.pocket_data).map((pocket_slug) => (
                                        <span key={`pocket_${o.id}_${pocket_slug}`}>
                                            <a
                                                href={url_reverse("pocket:item:edit", {
                                                    slug: pocket_slug,
                                                    item_id: o.pocket_data[pocket_slug].id,
                                                })}
                                                target="_blank"
                                            >
                                                {o.pocket_data[pocket_slug].name}
                                            </a>{" "}
                                        </span>
                                    ))}
                            </td>
                            <td>{o.is_launched && o.state_modified}</td>
                            <td>
                                <TableActionButtons item={o} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </ListingCard>
    );
}
const DeveloperModerMessages = ({ f_messages }) => {
    const { t } = useTranslation();
    return (
        <>
            <ListingCard header={t("Сообщения")}>
                <Table hover responsive className="dmg-table">
                    <thead>
                        <tr>
                            <th>{t("Дата")}</th>
                            <th>{t("Тип")}</th>
                            <th>{t("Текст")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {f_messages?.map((o) => (
                            <tr key={`message${o.id}`}>
                                <td>{o.date_added}</td>
                                <td>
                                    <Badge className="ml-auto" variant={o.sender === "developer" ? "success" : "info"}>
                                        {o.sender === "developer" ? t("от разработчика") : t("от модератора")}
                                    </Badge>
                                </td>
                                <td className="show-wrap-text">{parseAndLinkify(o.message)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </ListingCard>
        </>
    );
};

function Edit({ id }) {
    const kind = "launch";
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppApi(url_reverse("api:moderation:featuring:edit", { kind, featuring_id: id }));
    const onFormSubmit = useSubmit();
    const [show, setShow] = useState(false);
    const approveBtnRef = useRef(null);
    const rejectBtnRef = useRef(null);
    const [btnRef, setBtnRef] = useState(null);
    const commonInfo = useRouteLoaderData("commonInfo");
    const [blocks, setBlocks] = useState([
        {
            id: "block_slider_vkplay",
            name: t("Раздел «слайдер на VK Play»"),
            fields: ["slider_description", "slider_art", "slider_logo_vkplay"],
            comment_field: "block_slider_vkplay_moderator_comment",
        },
        {
            id: "block_popup",
            name: t("Поп-ап в лаунчере VK Play"),
            fields: ["popup_picture", "popup_logo", "popup_text"],
            comment_field: "block_popup_moderator_comment",
        },
        {
            id: "block_vk1",
            name: t("Баннер в рамках экосистемы VK #1"),
            fields: ["banner1_picture"],
            comment_field: "block_vk1_moderator_comment",
        },
        {
            id: "block_vk2",
            name: t("Баннер в рамках экосистемы VK #2"),
            fields: ["banner2_title", "banner2_descr", "banner2_picture", "banner2_picture_2"],
            comment_field: "block_vk2_moderator_comment",
        },
    ]);

    useEffect(() => {
        if (apiData?.forms_data) {
            const newBlocks = [...blocks];
            const filteredBlocks = newBlocks.filter(
                (block) =>
                    block.fields.find((field) => {
                        const fieldData = apiData.forms_data.main.data[field];
                        return fieldData == null || fieldData === "null" || fieldData === "";
                    }) === undefined
            );
            setBlocks(filteredBlocks);
        }
    }, [apiData]);

    if (!apiData) return;

    const state = apiData.object?.state;

    const handleSubmit = (ev) => {
        const newApiData = { ...apiData };
        if (ev.target.value === "approve") {
            newApiData.forms_data.main.data.state = GameFeaturingLaunchState.REVIEW_PASSED;
        } else if (ev.target.value === "reject") {
            newApiData.forms_data.main.data.state = GameFeaturingLaunchState.REJECTED;
        } else if (ev.target.value === "update" && newApiData.forms_data.main.data.is_launched) {
            newApiData.forms_data.main.data.state = GameFeaturingLaunchState.PROMO_LAUNCHED;
        } else if (ev.target.value === "update" && state === GameFeaturingLaunchState.REFUSED) {
            newApiData.forms_data.main.data.state = GameFeaturingLaunchState.MARKETING_REVIEW;
        }
        setApiData(newApiData);
        onFormSubmit(ev, { backUrl: url_reverse("admin:moderation:featuring:list", { kind }) });
    };

    const { game } = apiData;
    const header = game && (
        <div className={"d-flex"}>
            <div>
                <Link to={url_reverse("admin:moderation:featuring:list", { kind })}>{t("Базовое промо")}</Link> -{" "}
                <Link to={url_reverse("games:featuring:launch", { game_id: game.id })}>
                    {game.__model_locale_object.name}
                </Link>
            </div>

            <div className={"ml-auto"}>
                <GameFeaturingLaunchPromoStateDisplay state={state} />
            </div>
        </div>
    );

    const PreviewButton = ({ blockId, uuid }) => {
        if (!uuid || blockId === "block_vk1") {
            return <></>;
        }
        return (
            <DevForm.Button
                href={`//${commonInfo.domain_showcase}/promo_preview/${uuid}/?block=${blockId}`}
                className="ml-auto"
                size="sm"
                variant="primary"
                target="_blank"
                tooltip={t("Предпросмотр")}
            >
                <Awesome type="eye" />
            </DevForm.Button>
        );
    };

    return (
        <CommonCard header={header}>
            <Form>
                {blocks.map((block, index) => (
                    <Card key={`card-${index}`} className="mb-2">
                        <Card.Header className="d-flex">
                            <strong>{block.name}</strong>
                            <PreviewButton blockId={block.id} uuid={apiData.object.game_featuring_identifier} />
                        </Card.Header>
                        <Card.Body>
                            {block.fields.map((field) => (
                                <DevForm.Field key={`field-${field}`} prefix="main" name={field} hideInputs={true} />
                            ))}
                            {(state === GameFeaturingLaunchState.REJECTED ||
                                state === GameFeaturingLaunchState.MARKETING_REVIEW) && (
                                <DevForm.Field
                                    key={`field-${block.comment_field}`}
                                    prefix="comments"
                                    name={block.comment_field}
                                />
                            )}
                        </Card.Body>
                    </Card>
                ))}

                {state !== GameFeaturingLaunchState.REVIEW_PASSED &&
                    state !== GameFeaturingLaunchState.PROMO_LAUNCHED && (
                        <Row>
                            <Col>
                                <DevForm.Field prefix="main" name="refused" />
                                {apiData.forms_data.main.data?.refused && (
                                    <DevForm.Field prefix="comments" name={"refused_moderator_comment"} />
                                )}
                            </Col>
                        </Row>
                    )}

                {state === GameFeaturingLaunchState.REVIEW_PASSED && <DevForm.Field prefix="main" name="is_launched" />}
                <DevForm.Field prefix={"comments"} name={"message"} />
                {(state === GameFeaturingLaunchState.REVIEW_PASSED || state === GameFeaturingLaunchState.REFUSED) && (
                    <DevForm.Button variant="primary" type="submit" name="action" value="update" onClick={handleSubmit}>
                        {t("Сохранить")}
                    </DevForm.Button>
                )}

                {state === GameFeaturingLaunchState.MARKETING_REVIEW && (
                    <>
                        <Button
                            ref={approveBtnRef}
                            variant="success"
                            name="action"
                            value="approve"
                            onClick={() => {
                                setShow(true);
                                setBtnRef(approveBtnRef);
                            }}
                            disabled={apiData.forms_data.main.data?.refused}
                        >
                            {t("Одобрить")}
                        </Button>{" "}
                        <Button
                            ref={rejectBtnRef}
                            variant="danger"
                            name="action"
                            value="reject"
                            onClick={() => {
                                setShow(true);
                                setBtnRef(rejectBtnRef);
                            }}
                        >
                            {t("Отклонить")}
                        </Button>
                        {btnRef && (
                            <ConfirmModal
                                show={show}
                                setShow={setShow}
                                handle={handleSubmit}
                                buttonRef={btnRef}
                                setBtnRef={setBtnRef}
                            />
                        )}
                    </>
                )}
            </Form>
            &nbsp;
            {apiData?.f_messages && apiData.f_messages.length > 0 && (
                <DeveloperModerMessages f_messages={apiData.f_messages} />
            )}
        </CommonCard>
    );
}

export default function BasicPromoModerationScreen() {
    const { t } = useTranslation();
    const params = useParams();
    const header = t("Базовое промо");

    return <DefaultLayout>{params?.id ? <Edit id={params.id} /> : <List header={header} />}</DefaultLayout>;
}
